import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Eye, EyeOff, AlertCircle, Sparkles } from 'lucide-react'
import { useAuth } from '../components/auth/AuthProvider'

export function LoginPage() {
  const { isSignedIn, loading, signIn, signInWithCredentials } = useAuth()
  const navigate = useNavigate()

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [error, setError] = useState('')

  // Already signed in → go straight to chat
  useEffect(() => {
    if (!loading && isSignedIn) {
      navigate('/chat', { replace: true })
    }
  }, [isSignedIn, loading, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!username.trim() || !password.trim()) {
      setError('Please enter your username and password.')
      return
    }
    setError('')
    setSubmitting(true)
    try {
      const result = await signInWithCredentials(username.trim(), password)
      if (result.ok) {
        navigate('/chat', { replace: true })
      } else {
        setError(result.error || 'Invalid username or password.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleGoogle = async () => {
    setError('')
    setGoogleLoading(true)
    try {
      await signIn()
      // isSignedIn will flip → useEffect navigates to /chat
    } catch {
      setError('Google sign-in failed. Please try again.')
      setGoogleLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
      {/* Logo */}
      <div className="flex items-center gap-2 mb-8">
        <div className="relative">
          <Sparkles className="h-8 w-8 text-indigo-500 animate-pulse" />
          <div className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-400 rounded-full animate-ping" />
        </div>
        <span className="text-2xl font-semibold bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
          AIRA
        </span>
      </div>

      {/* Card */}
      <div className="w-full max-w-md bg-card border border-border/50 rounded-2xl shadow-xl p-8">
        {/* Heading */}
        <div className="text-center mb-7">
          <h1 className="text-2xl font-semibold text-foreground">Welcome back</h1>
          <p className="text-sm text-muted-foreground mt-1">Sign in to your account to continue</p>
        </div>

        {/* Credential form */}
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          {/* Username */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Username or Email
            </label>
            <input
              type="text"
              value={username}
              onChange={e => { setUsername(e.target.value); setError('') }}
              placeholder="Enter your username or email"
              autoComplete="username"
              disabled={submitting || googleLoading}
              className="w-full px-4 py-2.5 rounded-xl border-2 border-foreground/15 dark:border-white/15 bg-background text-foreground placeholder:text-muted-foreground text-sm focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-indigo-700 dark:focus:outline-indigo-400 focus:border-indigo-700/80 dark:focus:border-indigo-500 transition-all disabled:opacity-60"
            />
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => { setPassword(e.target.value); setError('') }}
                placeholder="Enter your password"
                autoComplete="current-password"
                disabled={submitting || googleLoading}
                className="w-full px-4 py-2.5 pr-11 rounded-xl border-2 border-foreground/15 dark:border-white/15 bg-background text-foreground placeholder:text-muted-foreground text-sm focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-indigo-700 dark:focus:outline-indigo-400 focus:border-indigo-700/80 dark:focus:border-indigo-500 transition-all disabled:opacity-60"
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowPassword(v => !v)}
                className="absolute right-3 inset-y-0 flex items-center text-muted-foreground hover:text-foreground transition-colors"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword
                  ? <EyeOff size={16} />
                  : <Eye size={16} />
                }
              </button>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 text-red-500 text-sm bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
              <AlertCircle size={14} className="flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting || googleLoading}
            className="w-full h-11 mt-1 rounded-xl bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white font-medium text-sm transition-all duration-150 hover:opacity-90 hover:scale-[1.01] active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed disabled:scale-100 focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-indigo-900/90 dark:focus:outline-indigo-200"
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Signing in…
              </span>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        {/* Divider */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-card px-3 text-xs text-muted-foreground">or continue with</span>
          </div>
        </div>

        {/* Google */}
        <button
          type="button"
          onClick={handleGoogle}
          disabled={googleLoading || submitting}
          className="w-full h-11 rounded-xl border border-border bg-background hover:bg-muted/40 text-foreground font-medium text-sm flex items-center justify-center gap-3 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-indigo-700 dark:focus:outline-indigo-400"
        >
          {googleLoading ? (
            <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
          ) : (
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
              <path d="M17.64 9.205c0-.638-.057-1.252-.164-1.84H9v3.48h4.844c-.208 1.125-.843 2.078-1.796 2.716v2.258h2.908C16.657 14.251 17.64 11.943 17.64 9.205z" fill="#4285F4"/>
              <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.71H.957v2.332C2.438 15.983 5.482 18 9 18z" fill="#34A853"/>
              <path d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9s.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
              <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
            </svg>
          )}
          Continue with Google
        </button>

        {/* Back link */}
        <div className="text-center mt-6">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors rounded-sm focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-indigo-700 dark:focus:outline-indigo-400"
          >
            ← Back to home
          </button>
        </div>
      </div>
    </div>
  )
}
